import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FaArrowsAlt,
  FaCamera,
  FaCheck,
  FaCropAlt,
  FaExpandArrowsAlt,
  FaMinus,
  FaPlus,
  FaRedo,
  FaSyncAlt,
  FaTimes,
  FaUndo,
  FaUpload,
  FaUser,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const VIEWPORT = 320;
const OUTPUT_SIZE = 480;

function parseUploadApiResponse(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function canvasToJpegFile(canvas, baseName) {
  const finish = (blob) => {
    if (!blob || blob.size === 0) {
      throw new Error("Could not encode image");
    }
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  };

  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        const m = /^data:image\/jpeg;base64,(.+)$/.exec(dataUrl);
        if (!m) {
          reject(new Error("Could not encode image"));
          return;
        }
        const bin = atob(m[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(finish(new Blob([arr], { type: "image/jpeg" })));
      } catch (err) {
        reject(err);
      }
    };

    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            try {
              resolve(finish(blob));
            } catch (e) {
              reject(e);
            }
          } else {
            fallback();
          }
        },
        "image/jpeg",
        0.92
      );
    } else {
      fallback();
    }
  });
}

function normalizedRotation(deg) {
  return ((deg % 360) + 360) % 360;
}

function computeCoverScale(img, rotation, viewport = VIEWPORT) {
  const rot = normalizedRotation(rotation);
  const w = rot === 90 || rot === 270 ? img.naturalHeight : img.naturalWidth;
  const h = rot === 90 || rot === 270 ? img.naturalWidth : img.naturalHeight;
  if (!w || !h) return 1;
  return Math.max(viewport / w, viewport / h);
}

function renderEditedCanvas(img, { rotation, scale, offsetX, offsetY, size }) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);

  const rot = normalizedRotation(rotation);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) throw new Error("Invalid image");

  const ratio = size / VIEWPORT;
  ctx.save();
  ctx.translate(size / 2 + offsetX * ratio, size / 2 + offsetY * ratio);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.scale(scale * ratio, scale * ratio);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();

  return canvas;
}

async function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

async function loadImageFromUrl(url, options = {}) {
  const { enrollmentNo, schoolCode, cacheBust } = options;

  const blobLooksLikeImage = async (blob) => {
    if (!(blob instanceof Blob) || blob.size === 0) return null;
    if (blob.type && blob.type.startsWith("application/json")) {
      return null;
    }
    if (
      blob.type.startsWith("image/") ||
      !blob.type ||
      blob.type === "application/octet-stream"
    ) {
      return blob;
    }
    // Some servers return wrong mime — sniff by trying to decode
    return blob;
  };

  const tryProxy = async () => {
    if (!enrollmentNo || !schoolCode) return null;
    let proxyUrl = `${API_BASE()}getStudentImageBlob.php?enrollment_no=${encodeURIComponent(
      enrollmentNo
    )}&school_code=${encodeURIComponent(schoolCode)}`;
    if (cacheBust != null && String(cacheBust) !== "") {
      proxyUrl += `&v=${encodeURIComponent(String(cacheBust))}`;
    }
    const { data } = await axios.get(proxyUrl, {
      responseType: "blob",
      withCredentials: true,
    });
    return blobLooksLikeImage(data);
  };

  let blob = await tryProxy().catch(() => null);
  if (!blob && url) {
    try {
      const { data } = await axios.get(url, {
        responseType: "blob",
        withCredentials: true,
      });
      blob = await blobLooksLikeImage(data);
    } catch {
      blob = null;
    }
  }

  if (!blob) {
    throw new Error("Could not load photo");
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Failed to load photo"));
      image.src = objectUrl;
    });
    return img;
  } finally {
    // Keep URL alive briefly so decode/paint can finish
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

function getStaffId() {
  const staff = SessionManager.getStaffDetails();
  return staff?.staff_id || "";
}

//Created By Cursor
export default function MdlEditStudentImage({
  open,
  student,
  imageUrl,
  cacheKey = 0,
  onClose,
  onSaved,
}) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [zoomPct, setZoomPct] = useState(100);
  const [hasNewFile, setHasNewFile] = useState(false);

  const baseScaleRef = useRef(1);

  const resetTransform = useCallback((img, rot = 0) => {
    if (!img) return;
    const cover = computeCoverScale(img, rot);
    baseScaleRef.current = cover;
    setRotation(rot);
    setScale(cover);
    setZoomPct(100);
    setOffsetX(0);
    setOffsetY(0);
  }, []);

  const loadSource = useCallback(
    async (file, url) => {
      setLoading(true);
      setReady(false);
      try {
        let img;
        if (file) {
          img = await loadImageFromFile(file);
          setHasNewFile(true);
        } else if (url || student?.student_enrollment_no) {
          img = await loadImageFromUrl(url || "", {
            enrollmentNo: student?.student_enrollment_no,
            schoolCode: SessionManager.getSchoolCode(),
            cacheBust: cacheKey,
          });
          setHasNewFile(false);
        } else {
          throw new Error("No image selected");
        }
        imgRef.current = img;
        resetTransform(img, 0);
        // Mount canvas first, then mark ready so preview can paint
        setLoading(false);
        setReady(true);
      } catch (error) {
        console.error("[MdlEditStudentImage] load error", error);
        myToast.notify_warning("Could not load image. Try re-uploading.");
        imgRef.current = null;
        setReady(false);
        setLoading(false);
      }
    },
    [cacheKey, resetTransform, student?.student_enrollment_no]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !student) return undefined;
    setRotation(0);
    setHasNewFile(false);
    const hasCandidate =
      Boolean(imageUrl) || Boolean(student.student_enrollment_no);
    if (hasCandidate) {
      loadSource(null, imageUrl || "");
    } else {
      imgRef.current = null;
      setReady(false);
      setLoading(false);
    }
    return undefined;
  }, [open, student, imageUrl, loadSource]);

  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !ready) return false;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;

    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, VIEWPORT, VIEWPORT);

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, VIEWPORT, VIEWPORT);
    ctx.clip();
    ctx.translate(VIEWPORT / 2 + offsetX, VIEWPORT / 2 + offsetY);
    ctx.rotate((normalizedRotation(rotation) * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.drawImage(
      img,
      -img.naturalWidth / 2,
      -img.naturalHeight / 2,
      img.naturalWidth,
      img.naturalHeight
    );
    ctx.restore();

    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, VIEWPORT - 2, VIEWPORT - 2);
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = "rgba(15,23,42,0.25)";
    ctx.strokeRect(8, 8, VIEWPORT - 16, VIEWPORT - 16);
    ctx.setLineDash([]);
    return true;
  }, [offsetX, offsetY, ready, rotation, scale]);

  useEffect(() => {
    if (!ready) return undefined;
    let raf = 0;
    let tries = 0;
    const paint = () => {
      if (drawPreview() || tries > 40) return;
      tries += 1;
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(raf);
  }, [drawPreview, ready]);

  const handleRotate = useCallback(
    (delta) => {
      const img = imgRef.current;
      if (!img) return;
      const nextRot = rotation + delta;
      const cover = computeCoverScale(img, nextRot);
      baseScaleRef.current = cover;
      setRotation(nextRot);
      setScale(cover);
      setZoomPct(100);
      setOffsetX(0);
      setOffsetY(0);
    },
    [rotation]
  );

  const handleZoom = useCallback((pct) => {
    const next = Math.min(300, Math.max(50, pct));
    setZoomPct(next);
    setScale((baseScaleRef.current * next) / 100);
  }, []);

  const handleReset = useCallback(() => {
    if (imgRef.current) resetTransform(imgRef.current, 0);
  }, [resetTransform]);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        myToast.notify_warning("Please choose an image file.");
        return;
      }
      loadSource(file, null);
      e.target.value = "";
    },
    [loadSource]
  );

  const handlePointerDown = useCallback(
    (e) => {
      if (!ready) return;
      e.preventDefault();
      dragRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        baseX: offsetX,
        baseY: offsetY,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [offsetX, offsetY, ready]
  );

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffsetX(dragRef.current.baseX + dx);
    setOffsetY(dragRef.current.baseY + dy);
  }, []);

  const handlePointerUp = useCallback((e) => {
    dragRef.current.active = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  const handleSave = useCallback(async () => {
    const img = imgRef.current;
    const enroll = student?.student_enrollment_no;
    const staffId = getStaffId();
    if (!img || !enroll) {
      myToast.notify_warning("Student enrollment is missing.");
      return;
    }
    if (!staffId) {
      myToast.notify_warning("Staff session is missing.");
      return;
    }

    setSaving(true);
    try {
      const canvas = renderEditedCanvas(img, {
        rotation,
        scale,
        offsetX,
        offsetY,
        size: OUTPUT_SIZE,
      });
      const baseName =
        String(enroll).replace(/[^\w-]/g, "_") || "student_photo";
      const file = await canvasToJpegFile(canvas, baseName);

      const formData = new FormData();
      formData.append("image", file);
      formData.append("enrollment_no", enroll);
      formData.append("staff_id", staffId);

      const response = await axios.post(
        `${API_BASE()}uploadStudentPic.php`,
        formData
      );
      const payload = parseUploadApiResponse(response.data);
      if (payload?.api_status === "OK") {
        myToast.notify_success(payload.api_remark || "Photo saved.");
        if (onSaved) onSaved();
        if (onClose) onClose();
      } else {
        myToast.notify_warning(payload?.api_remark || "Upload failed.");
      }
    } catch (error) {
      console.error("[MdlEditStudentImage] save error", error);
      const errPayload = parseUploadApiResponse(error.response?.data);
      myToast.notify_warning(
        errPayload?.api_remark || errPayload?.message || "Could not save photo."
      );
    } finally {
      setSaving(false);
    }
  }, [
    offsetX,
    offsetY,
    onClose,
    onSaved,
    rotation,
    scale,
    student?.student_enrollment_no,
  ]);

  const studentLabel = useMemo(() => {
    if (!student) return "";
    return student.stu_name || "Student";
  }, [student]);

  const studentMeta = useMemo(() => {
    if (!student) return [];
    const chips = [];
    if (student.student_enrollment_no) {
      chips.push(`ID ${student.student_enrollment_no}`);
    }
    if (student.student_roll_no) {
      chips.push(`Roll ${student.student_roll_no}`);
    }
    if (student.father_name) {
      chips.push(`S/O ${student.father_name}`);
    }
    return chips;
  }, [student]);

  if (!open || !student) return null;

  const modal = (
    <div className="msi-mdl" role="dialog" aria-modal="true" aria-labelledby="msi-mdl-title">
      <button
        type="button"
        className="msi-mdl__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="msi-mdl__panel">
        <header className="msi-mdl__hero">
          <div className="msi-mdl__hero-icon" aria-hidden>
            <FaCropAlt />
          </div>
          <div className="min-w-0">
            <h2 id="msi-mdl-title" className="msi-mdl__title">
              Edit student photo
            </h2>
            <p className="msi-mdl__sub">{studentLabel}</p>
            {studentMeta.length ? (
              <div className="msi-mdl__chips">
                {studentMeta.map((chip) => (
                  <span key={chip} className="msi-mdl__chip">
                    {chip}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="msi-mdl__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </header>

        <div className="msi-mdl__body">
          <div className="msi-mdl__editor">
            <div className="msi-mdl__viewport-shell">
              <div
                className={`msi-mdl__viewport${ready ? " is-ready" : ""}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                {loading ? (
                  <div className="msi-mdl__viewport-loading">
                    <FaSyncAlt className="msi-mdl__spin" aria-hidden />
                    <span>Loading photo…</span>
                  </div>
                ) : ready ? (
                  <canvas
                    ref={canvasRef}
                    width={VIEWPORT}
                    height={VIEWPORT}
                    className="msi-mdl__canvas"
                    aria-label="Photo crop preview"
                  />
                ) : (
                  <div className="msi-mdl__viewport-empty">
                    <span className="msi-mdl__empty-icon" aria-hidden>
                      <FaUser />
                    </span>
                    <p>No photo uploaded</p>
                    <span className="msi-mdl__empty-hint">
                      Upload a passport-style square image
                    </span>
                    <button
                      type="button"
                      className="msi-mdl__btn msi-mdl__btn--primary"
                      onClick={() => {
                        if (fileInputRef.current) fileInputRef.current.click();
                      }}
                    >
                      <FaUpload aria-hidden /> Upload photo
                    </button>
                  </div>
                )}
              </div>
              <span className="msi-mdl__corner msi-mdl__corner--tl" aria-hidden />
              <span className="msi-mdl__corner msi-mdl__corner--tr" aria-hidden />
              <span className="msi-mdl__corner msi-mdl__corner--bl" aria-hidden />
              <span className="msi-mdl__corner msi-mdl__corner--br" aria-hidden />
            </div>

            <p className="msi-mdl__hint">
              <FaArrowsAlt aria-hidden /> Drag to move
              <span className="msi-mdl__hint-dot" aria-hidden />
              <FaExpandArrowsAlt aria-hidden /> Square crop on save
            </p>
          </div>

          <aside className="msi-mdl__tools">
            <div className="msi-mdl__tool-group">
              <span className="msi-mdl__tool-label">
                <FaSyncAlt aria-hidden /> Transform
              </span>
              <div className="msi-mdl__tool-grid">
                <button
                  type="button"
                  className="msi-mdl__tool-btn"
                  onClick={() => handleRotate(-90)}
                  disabled={!ready || saving}
                  title="Rotate left"
                >
                  <FaUndo aria-hidden />
                  <span>Rotate L</span>
                </button>
                <button
                  type="button"
                  className="msi-mdl__tool-btn"
                  onClick={() => handleRotate(90)}
                  disabled={!ready || saving}
                  title="Rotate right"
                >
                  <FaRedo aria-hidden />
                  <span>Rotate R</span>
                </button>
                <button
                  type="button"
                  className="msi-mdl__tool-btn"
                  onClick={handleReset}
                  disabled={!ready || saving}
                  title="Reset position"
                >
                  <FaSyncAlt aria-hidden />
                  <span>Reset</span>
                </button>
              </div>
            </div>

            <div className="msi-mdl__tool-group">
              <label className="msi-mdl__tool-label" htmlFor="msi-zoom">
                <FaExpandArrowsAlt aria-hidden /> Resize · {zoomPct}%
              </label>
              <div className="msi-mdl__zoom-row">
                <button
                  type="button"
                  className="msi-mdl__zoom-btn"
                  onClick={() => handleZoom(zoomPct - 10)}
                  disabled={!ready || saving || zoomPct <= 50}
                  aria-label="Zoom out"
                >
                  <FaMinus />
                </button>
                <input
                  id="msi-zoom"
                  type="range"
                  className="msi-mdl__range"
                  min={50}
                  max={300}
                  step={1}
                  value={zoomPct}
                  onChange={(e) => handleZoom(Number(e.target.value))}
                  disabled={!ready || saving}
                />
                <button
                  type="button"
                  className="msi-mdl__zoom-btn"
                  onClick={() => handleZoom(zoomPct + 10)}
                  disabled={!ready || saving || zoomPct >= 300}
                  aria-label="Zoom in"
                >
                  <FaPlus />
                </button>
              </div>
            </div>

            <div className="msi-mdl__tool-group">
              <span className="msi-mdl__tool-label">
                <FaCamera aria-hidden /> Photo file
              </span>
              <button
                type="button"
                className="msi-mdl__btn msi-mdl__btn--upload"
                onClick={() => {
                  if (fileInputRef.current) fileInputRef.current.click();
                }}
                disabled={saving}
              >
                <FaUpload aria-hidden />
                {hasNewFile ? "Change upload" : "Re-upload"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </div>
          </aside>
        </div>

        <footer className="msi-mdl__footer">
          <button
            type="button"
            className="msi-mdl__btn msi-mdl__btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="msi-mdl__btn msi-mdl__btn--primary"
            onClick={handleSave}
            disabled={!ready || saving}
          >
            {saving ? (
              <>
                <FaSyncAlt className="msi-mdl__spin" aria-hidden /> Saving…
              </>
            ) : (
              <>
                <FaCheck aria-hidden /> Save photo
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
